/**
 * Speech-to-text, behind an interface.
 *
 * The locked decision is browser Web Speech first, with a server STT swappable in later for
 * accuracy. So nothing above this file knows what is doing the transcribing: the dialogue layer
 * asks for a recogniser, holds it while the player holds the key, and gets text back.
 *
 * Two things this interface deliberately does *not* do:
 *
 * - **It never decides what the player meant.** Transcription and interpretation are separate
 *   problems with separate failure modes; the matcher (P4.2) handles the second, and keeping them
 *   apart is what lets either be replaced without touching the other.
 * - **It never blocks the game.** Every failure — no support, no permission, no microphone, no
 *   network — resolves to "no transcript", because the authored options are always clickable.
 *   Speech is an input method, not a requirement.
 */

/** What went wrong, in terms the UI can explain to a player. */
export type SpeechErrorKind =
  | 'unsupported'
  | 'permission-denied'
  | 'no-microphone'
  | 'no-speech'
  | 'network'
  | 'aborted'
  | 'unknown';

export class SpeechError extends Error {
  constructor(
    readonly kind: SpeechErrorKind,
    message?: string,
  ) {
    super(message ?? kind);
    this.name = 'SpeechError';
  }
}

export interface SpeechResult {
  transcript: string;
  /** 0–1 as reported by the engine. Web Speech is often optimistic; treat as a hint. */
  confidence: number;
  /** True once the engine has settled on this text. */
  isFinal: boolean;
}

export interface SpeechHandlers {
  /** Fired repeatedly with interim text, so the player can see they are being heard. */
  onPartial?: (result: SpeechResult) => void;
  /** Fired once when capture ends, with the best final transcript (empty if nothing was heard). */
  onFinal: (result: SpeechResult) => void;
  onError?: (error: SpeechError) => void;
}

export interface SpeechRecogniser {
  /** Whether this recogniser can actually run here. */
  readonly available: boolean;
  /** Human-readable id for diagnostics: `web-speech`, `none`, later `server`. */
  readonly id: string;
  readonly isCapturing: boolean;
  /** Begin capture. Resolves once the engine is listening, rejects with a SpeechError. */
  start(handlers: SpeechHandlers): Promise<void>;
  /** Stop capture and let the engine settle — `onFinal` follows. */
  stop(): void;
  /** Stop capture and discard whatever was heard. No `onFinal`. */
  abort(): void;
  dispose(): void;
}

/**
 * The recogniser used where speech is unavailable.
 *
 * A null object rather than a null check: every caller would otherwise need to branch, and the
 * one that forgot would be the one that broke the game for a Firefox user. Its `onFinal` is
 * never called, so the UI simply stays on the non-voice path.
 */
export class UnavailableRecogniser implements SpeechRecogniser {
  readonly available = false;
  readonly id = 'none';
  readonly isCapturing = false;

  async start(handlers: SpeechHandlers): Promise<void> {
    const error = new SpeechError('unsupported', 'speech recognition is not available here');
    handlers.onError?.(error);
    throw error;
  }

  stop(): void {}
  abort(): void {}
  dispose(): void {}
}
